import {Component, Input} from "@angular/core";
import {LocalizationStrings} from "../../../../i18n/i18n-model";
import {SleepReportMeasurements, SleepReportPosition} from "../../sleep-report/sleep-report-data";
import {
  formatAmountWithIndex,
  formatPercentage,
  formatPercentageWithDuration,
  formatReportNumber
} from "../../sleep-report/sleep-report-formatting";

interface PositionColumn {
  label: keyof LocalizationStrings;
  position: SleepReportPosition;
}

interface PositionRow {
  label: keyof LocalizationStrings;
  value: (position: SleepReportPosition) => string;
}

interface MeasurementRow {
  label: keyof LocalizationStrings;
  value: string;
  isPieces: boolean;
}

@Component({
  selector: "app-sleep-measurements",
  templateUrl: "./sleep-measurements.component.html",
  standalone: false
})
export class SleepMeasurementsComponent {
  @Input() measurements!: SleepReportMeasurements;
  @Input() reportLanguage!: string;

  readonly positionRows: PositionRow[] = [
    {label: "sleepReportLabelSleepTimeDuration", value: (position) => position.sleepTimeDuration},
    {label: "sleepReportLabelSleepTimeShare", value: (position) => this.percentage(position.sleepTimePercentage)},
    {label: "sleepReportLabelAhi", value: (position) => this.number(position.ahiIndex)},
    {label: "sleepReportLabelObstructiveApneas", value: (position) => this.amountWithIndex(position.oaAmount, position.oaIndex)},
    {label: "sleepReportLabelCentralApneas", value: (position) => this.amountWithIndex(position.caAmount, position.caIndex)},
    {label: "sleepReportLabelMixedApneas", value: (position) => this.amountWithIndex(position.maAmount, position.maIndex)},
    {label: "sleepReportLabelHypopneas", value: (position) => this.amountWithIndex(position.hypoAmount, position.hypoIndex)},
    {label: "sleepReportLabelDesaturations", value: (position) => this.amountWithIndex(position.deSatAmount, position.deSatIndex)},
  ];

  positionColumns(): PositionColumn[] {
    const positions = this.measurements.positions;

    return [
      {label: "sleepReportLabelSupine", position: positions.supine},
      {label: "sleepReportLabelNotSupine", position: positions.notSupine},
      {label: "sleepReportLabelLeft", position: positions.left},
      {label: "sleepReportLabelRight", position: positions.right},
      {label: "sleepReportLabelProne", position: positions.prone},
      {label: "sleepReportLabelUpright", position: positions.upright},
    ];
  }

  oxygenRows(): MeasurementRow[] {
    const o2Levels = this.measurements.o2Levels;

    return [
      {label: "sleepReportLabelSpO2Average", value: this.percentage(o2Levels.spO2Average), isPieces: false},
      {label: "sleepReportLabelSpO2Minimum", value: this.percentage(o2Levels.spO2Minimum), isPieces: false},
      {label: "sleepReportLabelSpO2Baseline", value: this.percentage(o2Levels.spO2Baseline), isPieces: false},
      {label: "sleepReportLabelDesaturationsBelow90", value: o2Levels.deSatBelow90, isPieces: true},
      {label: "sleepReportLabelDesaturationsBelow80", value: o2Levels.deSatBelow80, isPieces: true},
      {
        label: "sleepReportLabelSpO2Below90",
        value: formatPercentageWithDuration(this.number(o2Levels.spO2Percentage), o2Levels.spO2Duration),
        isPieces: false
      },
    ];
  }

  breathingAndHeartRows(): MeasurementRow[] {
    const measurements = this.measurements;

    return [
      {label: "sleepReportLabelSnoringShare", value: this.percentage(measurements.snoringPercentage), isPieces: false},
      {label: "sleepReportLabelBreathingFrequency", value: measurements.averageBreathingFrequency, isPieces: false},
      {label: "sleepReportLabelCheyneStokes", value: this.percentage(measurements.cheyneStokesPercentage), isPieces: false},
      {label: "sleepReportLabelHeartRateAverage", value: measurements.hrAverage, isPieces: false},
      {label: "sleepReportLabelHeartRateMinimum", value: measurements.hrMin, isPieces: false},
      {label: "sleepReportLabelHeartRateMaximum", value: measurements.hrMax, isPieces: false},
    ];
  }

  private percentage(value: string): string {
    return formatPercentage(this.number(value));
  }

  private amountWithIndex(amount: string, index: string): string {
    return formatAmountWithIndex(amount, index, this.reportLanguage);
  }

  number(value: string): string {
    return formatReportNumber(value, this.reportLanguage);
  }
}
